"use client"

import styled from "@emotion/styled"
import { Tab, Tabs } from "@mui/material"
import { useTranslations } from "next-intl"
import { useState } from "react"
import { Container, NewText } from "ui"
import { sharedColors } from "ui/theme/colors"
import { Apartment, PriorityData, ProjectPage, filterClick } from "utils"
import { ProjectsMessages } from "../Projects"
import WhiteBgHeading from "./ApartmentMarketingForRent/WhiteBgHeading"
import PropertyTab from "./PropertyTab"

const StyledContainer = styled.div<{ $paddingTop: boolean; $paddingBottom?: boolean }>`
  font-family: var(--narkis) !important;
  padding-top: ${(p) => (p.$paddingTop ? "60px" : "32px")};
  padding-bottom: ${(p) => (p.$paddingBottom ? "60px" : "32px")};
  gap: 32px;
  display: flex;
  flex-direction: column;
  ${(props) => props.theme.breakpoints.up("md")} {
    padding-top: ${(p) => (p.$paddingTop ? "60px" : "0px")};
    padding-bottom: ${(p) => (p.$paddingBottom ? "60px" : "0px")};
    gap: 24px;
  }
`
const TopText = styled.div<{ textCenter?: boolean }>`
  display: flex;
  flex-direction: column;
  text-align: center;
  margin: auto;
  gap: 8px;
  align-items: ${(props) => (props.textCenter ? "center" : "initial")};
  ${(props) => props.theme.breakpoints.down("sm")} {
    ul {
      padding-right: 16px;
      padding-left: 16px;
    }
  }

  ${(props) => props.theme.breakpoints.up("md")} {
    gap: 16px;
    text-align: left;
    margin: 0;
  }
`

const TabsContainer = styled(Tabs)`
  .MuiTabs-flexContainer {
    flex-wrap: wrap;
    gap: 8px;
    justify-content: center;
    ${(props) => props.theme.breakpoints.up("md")} {
      gap: 16px;
      justify-content: flex-start;
    }
  }
`

const StyledTab = styled(Tab)<{ active: boolean }>`
  font-size: 16px;
  line-height: 20px;
  padding: 14px 16px;
  border-radius: 8px;
  color: black;
  font-weight: ${(props) => (props.active ? "600" : "400")};
  border: ${(props) => (props.active ? "2px" : "1px")} solid black;
  text-transform: none;
  ${(props) => props.theme.breakpoints.up("md")} {
    font-size: 18px;
    color: black;
    line-height: 25.92px;
    padding: 7px 16px;
  }
`
const ApartmentsContainer = styled.div<{ value: number }>`
  display: flex;
  flex-direction: column;
  gap: ${(props) => (props.value !== 0 ? "32px" : "0px")};
  ${(props) => props.theme.breakpoints.up("md")} {
    gap: ${(props) => (props.value !== 0 ? "40px" : "0px")};
  }
`

export function a11yProps(index: number) {
  return {
    id: `simple-tab-${index}`,
    "aria-controls": `simple-tabpanel-${index}`,
  }
}
type TabProps = {
  value: number
  index: number
  apartments: Apartment[]
  locale: string
  formSubjects: Record<string, string>
  messages: ProjectsMessages
  bmbyId?: number
  bmbyMeetingId?: number | string
  status?: any
  el?: any
  allApChar: string[]
  allBuChar: string[]
  imageGalleries: {
    apartmentThumbnail?: string
    apartmentGallery?: string[]
    numberOfRooms?: number
  }[]
  companyId?: number
  buildingCharacteristicsText?: string
  isMarketingRent?: boolean
  fontFamilyVariable?: string
  phone?: string
  rentProjectInterestedNum?: number
}

function ActiveTab({
  index,
  value,
  apartments,
  locale,
  formSubjects,
  messages,
  bmbyId,
  status,
  allApChar,
  allBuChar,
  imageGalleries,
  companyId,
  isMarketingRent,
  buildingCharacteristicsText,
  bmbyMeetingId,
  fontFamilyVariable,
  phone,
  rentProjectInterestedNum,
}: TabProps) {
  return (
    <div
      role="tabpanel"
      hidden={value !== 0 && value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      style={value === 0 && index === 0 && !isMarketingRent ? { marginTop: "40px" } : {}}
      className={fontFamilyVariable || ""}
    >
      {(value === 0 || value === index) && (
        <PropertyTab
          companyId={companyId}
          apartments={apartments}
          imageGalleries={imageGalleries}
          locale={locale}
          formSubjects={formSubjects}
          messages={messages}
          showHeader={value !== 0 || index === 0}
          bmbyId={bmbyId}
          bmbyMeetingId={bmbyMeetingId}
          status={status}
          allApChar={allApChar}
          allBuChar={allBuChar}
          isMarketingRent={isMarketingRent}
          buildingCharacteristicsText={buildingCharacteristicsText}
          fontFamilyVariable={fontFamilyVariable}
          phone={phone}
          rentProjectInterestedNum={rentProjectInterestedNum}
        />
      )}
    </div>
  )
}

type Props = {
  projectData: ProjectPage
  priorityData: PriorityData
  locale: string
  formSubjects: Record<string, string>
  messages: ProjectsMessages
  allApChar: string[]
  allBuChar: string[]
  companyId?: number
  isMarketingRent?: boolean
  fontFamilyVariable?: string
  rentProjectInterestedNum?: number
}

function Apartments({
  projectData,
  locale,
  formSubjects,
  messages,
  priorityData,
  allApChar,
  allBuChar,
  companyId,
  isMarketingRent,
  fontFamilyVariable,
  rentProjectInterestedNum,
}: Props) {
  const [value, setValue] = useState(0)
  const t = useTranslations("BuildingInfo")
  const { apartments, types } = priorityData

  const title = isMarketingRent
    ? projectData?.newTitleAboveProperty || ""
    : projectData?.titleAboveProperty

  const handleChange = (event: React.SyntheticEvent, newValue: number) => {
    setValue(newValue)
    if (types?.[newValue - 1]?.value) {
      filterClick(types[newValue - 1].value, "פילטר סוג פרוייקט בעמוד פרוייקטים")
    }
  }
  return (
    <Container>
      <StyledContainer
        $paddingTop={
          title?.length > 0 || projectData.textAboveProperty.length > 0 || apartments.length > 0
        }
        $paddingBottom={isMarketingRent}
      >
        {(title?.length > 0 || projectData.textAboveProperty.length > 0) &&
          (isMarketingRent ? (
            <WhiteBgHeading
              title={title}
              subTitle={projectData?.textAboveProperty}
              mobileMarginBottom={32}
              desktopMarginBottom={32}
              isSubTitleRichText={true}
            />
          ) : (
            <TopText>
              <NewText
                component={"h2"}
                desktopFontSize={32}
                desktopLineHeight={"36px"}
                mobileFontSize={24}
                mobileLineHeight={"28px"}
                color={sharedColors.black}
              >
                {title}
              </NewText>
              <NewText
                component={"div"}
                desktopFontSize={18}
                desktopLineHeight={"25.92px"}
                mobileFontSize={16}
                mobileLineHeight={"23px"}
                color={sharedColors.black}
                dangerouslySetInnerHTML={{
                  __html: projectData.textAboveProperty,
                }}
              />
            </TopText>
          ))}
        {apartments.length > 0 && (
          <ApartmentsContainer value={value}>
            {types.length > 1 && (
              <TabsContainer value={value} onChange={handleChange} aria-label="property types">
                <StyledTab
                  active={value === 0}
                  key={"all"}
                  label={t("showAll")}
                  style={{ fontSize: 20 }}
                />
                {types.map((el, index) => (
                  <StyledTab
                    active={index + 1 === value}
                    key={index + 1}
                    label={el.value}
                    {...a11yProps(index)}
                    style={{ fontSize: 20 }}
                  />
                ))}
              </TabsContainer>
            )}
            {value === 0 ? (
              <ActiveTab
                locale={locale}
                apartments={apartments}
                imageGalleries={projectData.imageGalleries}
                index={0}
                value={value}
                formSubjects={formSubjects}
                messages={messages}
                bmbyId={Number(projectData?.priorityID)}
                phone={projectData?.phone}
                status={projectData?.projectStatuses[0]?.name}
                allApChar={allApChar}
                allBuChar={allBuChar}
                companyId={companyId}
                isMarketingRent={isMarketingRent}
                buildingCharacteristicsText={projectData?.buildingCharacteristicsText}
                bmbyMeetingId={projectData.bmbyMeetingId}
                fontFamilyVariable={fontFamilyVariable}
                rentProjectInterestedNum={rentProjectInterestedNum}
              />
            ) : (
              types.map((el, index) => {
                return (
                  <ActiveTab
                    key={index}
                    imageGalleries={projectData.imageGalleries}
                    locale={locale}
                    apartments={apartments.filter((ap) => ap.propertyType === el.value)}
                    index={index + 1}
                    value={value}
                    el={el}
                    formSubjects={formSubjects}
                    messages={messages}
                    bmbyId={Number(projectData?.priorityID)}
                    status={projectData?.projectStatuses[0]?.name}
                    allApChar={allApChar}
                    allBuChar={allBuChar}
                    companyId={companyId}
                    isMarketingRent={isMarketingRent}
                    buildingCharacteristicsText={projectData?.buildingCharacteristicsText}
                    rentProjectInterestedNum={rentProjectInterestedNum}
                  />
                )
              })
            )}
          </ApartmentsContainer>
        )}
      </StyledContainer>
    </Container>
  )
}

export default Apartments
